import { HistoryOutlined } from '@ant-design/icons';
import { useNavigate } from '@tanstack/react-router';
import {
    Button,
    Col,
    Form,
    type FormInstance,
    Input,
    InputNumber as InputNumberAntd,
    Row,
    Switch,
} from 'antd';
import type { FormServicoValues } from '@/features/servico/types/Servico';
import type { FormAction } from '@/types/FormAction';
import { Container } from '@/components/Container';
import { InputNumber } from '@/components/input/InputNumber';
import { CustomSwitch } from '@/components/revisar/CustomSwitch';
import { SelectMarcador } from '@/features/marcador/components/SelectMarcador';
import { MarcadorLocal } from '@/features/marcador/types/enum';
import { SelectRegraFiscal } from '@/features/regra-fiscal/components/SelectRegraFiscal';
import { RegraFiscalTipo } from '@/features/regra-fiscal/types/RegraFiscal';
import { roundAbnt } from '@/std/math';
import { ServicoTabKey, ServicoTabKeyHistoricoAlteracao } from '../useServicoTabItems';

export function ServicoTabGeral({
    form,
    action,
}: {
    form: FormInstance<FormServicoValues>;
    action: FormAction;
}) {
    const navigate = useNavigate({ from: '/a/servico/$action/{-$idpk}' });

    const onChangePrecoVenda = (): void => {
        const markup: number = form.getFieldValue('pro_markup_venda');
        if (markup === 0) {
            return;
        }

        const custo: number = form.getFieldValue('pro_preco_custo');
        const venda: number = form.getFieldValue('pro_preco_venda');
        const calculo = roundAbnt(((venda - custo) / custo) * 100);
        form.setFieldsValue({ pro_markup_venda: calculo });
    };

    return (
        <Container>
            <Form.Item hidden={true} name='pro_tipo'>
                <Input />
            </Form.Item>

            <Form.Item hidden={true} name='pro_produto_pai_idpk'>
                <Input type='hidden' />
            </Form.Item>

            <Row gutter={[8, 8]}>
                <Col lg={16} md={24} sm={12} xs={24}>
                    <Form.Item
                        className='mb-3'
                        label='Nome do serviço'
                        name='pro_descricao'
                        rules={[
                            { message: 'Por favor, forneça o nome do serviço', required: true },
                        ]}
                    >
                        <Input />
                    </Form.Item>
                </Col>

                <Col lg={8} md={12} sm={12} xs={24}>
                    <Form.Item
                        label='Código Interno'
                        name='pro_codigo_interno'
                        tooltip='Código para auxiliar na busca de serviço e localização. Deixando vazio o sistema atribui um número sequencial automaticamente.'
                    >
                        <InputNumberAntd className='w-full' />
                    </Form.Item>
                </Col>

                <Col lg={6} md={12} sm={6} xs={24}>
                    <Form.Item className='mb-3' label='Preço de Venda' name='pro_preco_venda'>
                        <InputNumber
                            addonAfter={
                                action === 'editar' ? (
                                    <Button
                                        icon={<HistoryOutlined />}
                                        onClick={() => {
                                            void navigate({
                                                search: {
                                                    tab1: ServicoTabKey.HistoricoAlteracoes,
                                                    tab2: ServicoTabKeyHistoricoAlteracao.PrecoVenda,
                                                },
                                            });
                                        }}
                                        title='Histórico de preços'
                                    />
                                ) : undefined
                            }
                            decimalScale={4}
                            onChange={onChangePrecoVenda}
                            type='$'
                        />
                    </Form.Item>
                </Col>

                <Col lg={9} md={12} sm={12} xs={24}>
                    <Form.Item
                        label='Grupo de regras fiscais'
                        name='pro_produto_regra_fiscal_idpk'
                        tooltip="Informe o grupo de regras fiscais para facilitar o preenchimento dos impostos na NFS-e. Cadastro via menu 'Fiscal->Regras fiscais'"
                    >
                        <SelectRegraFiscal tipo={RegraFiscalTipo.Servico} />
                    </Form.Item>
                </Col>

                <Col lg={9} md={12} sm={24} xs={24} xxl={8}>
                    <Form.Item
                        label='Marcadores'
                        name='pro_marcadores'
                        tooltip='Para cadastrar categorias acesse: Configurações > Cadastros > Marcadores'
                    >
                        <SelectMarcador local={MarcadorLocal.Produtos} />
                    </Form.Item>
                </Col>

                <Col lg={3} md={8} sm={12} xs={24}>
                    <Form.Item
                        getValueFromEvent={(e) => (e ? 1 : 2)}
                        getValueProps={(e) => ({ value: e === 1 })}
                        label='Status'
                        name='pro_inativo'
                        valuePropName='checked'
                    >
                        <Switch checkedChildren='Ativo' unCheckedChildren='Inativo' />
                    </Form.Item>
                </Col>

                <Col lg={3} md={8} sm={12} xs={24}>
                    <Form.Item label='Favorito' name='pro_favorito'>
                        <CustomSwitch checked='Sim' unchecked='Não' />
                    </Form.Item>
                </Col>

                <Col lg={6} md={8} sm={12} xs={24}>
                    <Form.Item label='Permite mudar preço na venda' name='pro_permite_mudar_preco'>
                        <CustomSwitch checked='Sim' unchecked='Não' />
                    </Form.Item>
                </Col>
            </Row>
        </Container>
    );
}
